/**
 * TeamPrintPage - Printable team papers (joukkuelaput).
 *
 * Opens in new tab, displays A4 landscape pages ready for printing.
 * Each team gets one page with their schedule and QR code.
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { printApi, mediaApi, contactsApi } from '@/api/client';
import type { TeamPrintData, PrintAssets, PrintContacts } from '@/types';

import './print.css';

interface TeamPaperProps {
  team: TeamPrintData;
  index: number;
  total: number;
  assets: PrintAssets | undefined;
  contacts: PrintContacts | undefined;
}

function TeamPaper({ team, index, total, assets, contacts }: TeamPaperProps) {
  const leagueLabels: Record<string, string> = {
    yle: 'Y',
    pro: 'P',
    frm: 'F',
  };

  const isFirma = team.league_id === 'frm';

  // Get logos from assets, fallback to empty array
  const clubLogos = assets?.club_logos || [];
  const kyykkaLogos = assets?.kyykka_logos || [];
  const advertisements = assets?.advertisements || [];

  return (
    <div className="print-page a4-landscape">
      {/* Header */}
      <div className="print-header">
        <div className="print-header-left">
          <span className="print-league-badge">{leagueLabels[team.league_id] || team.league_id}</span>
          <h1 className="print-title">{team.team_name}</h1>
          
          {isFirma ? (
            <>
              <p className="print-subtitle">DO NOT MISPLACE THIS PAPER</p>
              <p className="print-info">The best team in the group-stage will continue to playoffs.</p>
              <p className="print-info">Results and information on next matches is published at https://app.kyykka.fi (QR-code).</p>
            </>
          ) : (
            <>
              <p className="print-subtitle">ÄLÄ HUKKAA TÄTÄ PAPERIA</p>
              {team.contact_name && (
                <p className="print-info">
                  Tilaaja: {team.contact_name}
                  {team.contact_phone && `, ${team.contact_phone}`}.
                </p>
              )}
              {team.league_id === 'pro' ? (
                <p className="print-info">Alkulohkon neljä parasta joukkuetta jatkaa pudotuspeleihin.</p>
              ) : (
                <p className="print-info">
                  Lohkon voittaja siirtyy ohjeen mukaan seuraavalle kentälle. 
                  Alkulohkoa seuraa kolmen joukkueen jatkolohko, jonka jälkeen alkaa pudotuspelit.
                </p>
              )}
              <p className="print-info">Tulokset ja tieto jatkopeleistä julkaistaan https://app.kyykka.fi (QR-koodi).</p>
            </>
          )}
        </div>

        <div className="print-header-right">
          {/* Kyykkä logos from media assets */}
          {kyykkaLogos.map((logo) => (
            <img 
              key={logo.id}
              className="print-logo" 
              src={logo.file_url} 
              alt={logo.name}
            />
          ))}
          {/* Club logos from media assets (rightmost) */}
          {clubLogos.map((logo) => (
            <img 
              key={logo.id}
              className="print-logo" 
              src={logo.file_url} 
              alt={logo.name}
            />
          ))}
        </div>
      </div>

      {/* Schedule table */}
      <table className="print-table">
        <thead>
          <tr>
            <th>{isFirma ? 'Time' : 'Klo'}</th>
            <th>{isFirma ? 'Field' : 'Kenttä'}</th>
            <th>{isFirma ? 'Team 1' : 'Joukkue 1'}</th>
            <th>{isFirma ? 'Team 2' : 'Joukkue 2'}</th>
          </tr>
        </thead>
        <tbody>
          {team.games.map((game) => {
            const isTeam1 = game.team1_name === team.team_name;
            const isTeam2 = game.team2_name === team.team_name;
            const isPlaying = isTeam1 || isTeam2;
            const timeCell = `${game.schedule_start} - ${game.schedule_end}`;
            return (
              <tr key={game.id}>
                <td className="time">{isPlaying ? <strong>{timeCell}</strong> : timeCell}</td>
                <td className="field">{game.court_id || '-'}</td>
                <td className="team">{isTeam1 ? <strong>{game.team1_name}</strong> : game.team1_name}</td>
                <td className="team">{isTeam2 ? <strong>{game.team2_name}</strong> : game.team2_name}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Playoff pathway */}
      <div className="print-pathway">
        <h3>
          {isFirma 
            ? 'If you qualify from the group stage, you will continue with following timetable:' 
            : 'Jos voitatte lohkon, siirrytte seuraavalle kentälle tämän aikataulun mukaisesti:'
          }
        </h3>
        
        {team.pathway && team.pathway.length > 0 ? (
          <table className="print-pathway-table">
            <tbody>
              <tr>
                <td className="time-label"><b>{isFirma ? 'Time' : 'Klo'}</b></td>
                {team.pathway.map((step, i) => (
                  <td key={`time-${step.paper_id}`} className="playoff" style={i === 0 ? { fontWeight: 'bold' } : undefined}>
                    {step.schedule_start} - {step.schedule_end}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="field-label"><b>{isFirma ? 'Field' : 'Kenttä'}</b></td>
                {team.pathway.map((step, i) => (
                  <td key={`field-${step.paper_id}`} className="playoff" style={i === 0 ? { fontWeight: 'bold' } : undefined}>
                    <a href={`/P${step.paper_id}`}>{step.court_id}</a>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        ) : (
          <p className="print-info">
            {isFirma 
              ? 'After the group ends, follow https://app.kyykka.fi (QR-code) to receive information on the next match.'
              : 'Lohkon päätyttyä, tarkkaile https://app.kyykka.fi (QR-koodi) saadaksesi tiedon jatkopeleistä.'
            }
          </p>
        )}
      </div>

      {/* Bottom bar - team info, sponsors, QR code */}
      <div className="print-bottom-bar">
        <div className="print-bottom-left">
          <strong>{team.team_name}</strong>
          <span>{index + 1}/{total} - {team.store_id}</span>
          {isFirma && contacts?.paatuomari_firma[0] && (
            <span><strong>Päätuomari:</strong> {contacts.paatuomari_firma[0].name} {contacts.paatuomari_firma[0].phone}</span>
          )}
        </div>

        {/* Sponsor logos from media assets */}
        <div className="print-sponsors">
          {advertisements.map((ad) => (
            <img 
              key={ad.id}
              className="print-sponsor-logo" 
              src={ad.file_url} 
              alt={ad.name}
            />
          ))}
        </div>

        <div className="print-qr-container">
          <div style={{ textAlign: 'center', fontSize: '0.7em' }}>Tulokset | Results</div>
          <img className="print-qr" src={team.qr_code} alt="QR Code" />
        </div>
      </div>
    </div>
  );
}

export function TeamPrintPage() {
  const [searchParams] = useSearchParams();
  const league = searchParams.get('league') || undefined;
  const teamId = searchParams.get('id');

  // Set print mode class on body
  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => {
      document.body.classList.remove('print-mode');
    };
  }, []);

  // Fetch print data
  const { data: teams, isLoading: teamsLoading, error: teamsError } = useQuery({
    queryKey: ['print-teams', league, teamId],
    queryFn: async () => {
      if (teamId) {
        const team = await printApi.getTeam(parseInt(teamId, 10));
        return [team];
      }
      return printApi.getTeams(league);
    },
  });

  // Fetch media assets
  const { data: assets, isLoading: assetsLoading } = useQuery({
    queryKey: ['print-assets'],
    queryFn: () => mediaApi.getPrintAssets(),
  });

  // Fetch contacts
  const { data: contacts, isLoading: contactsLoading } = useQuery({
    queryKey: ['print-contacts'],
    queryFn: () => contactsApi.getPrintContacts(),
  });

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    window.close();
  };

  const isLoading = teamsLoading || assetsLoading || contactsLoading;

  if (isLoading) {
    return (
      <div className="print-controls">
        <span>Ladataan...</span>
      </div>
    );
  }

  if (teamsError || !teams) {
    return (
      <div className="print-controls">
        <span>Virhe ladattaessa tietoja</span>
        <button className="secondary" onClick={handleClose}>Sulje</button>
      </div>
    );
  }

  return (
    <>
      {/* Print controls */}
      <div className="print-controls">
        <button onClick={handlePrint}>🖨️ Tulosta ({teams.length} sivua)</button>
        <button className="secondary" onClick={handleClose}>Sulje</button>
      </div>

      {/* Team papers */}
      {teams.map((team, index) => (
        <TeamPaper 
          key={team.id} 
          team={team} 
          index={index} 
          total={teams.length} 
          assets={assets}
          contacts={contacts}
        />
      ))}
    </>
  );
}
